<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed, watch, onMounted, ref } from 'vue'
import { useLogger } from 'vue-logger-plugin'
import type { IAoeNewUserWizard } from '../../types/WizardTypes'
import { TermsNUWGet, TermsNUWCreate } from '../../api/ContractAgreement.ts'
import { GetBeeNumberAuthenicatedUser } from '../../stores/SettingsStore.ts'
import { SetIsLoading } from '../../services/AoeHelpers.ts'
import { PwsProfileTermsGetList } from '../../api/ConsultantCorner.ts'

const { t } = useI18n()
const logger = useLogger()

// Props defined explicitly to avoid TypeScript errors
const props = defineProps({
  formData: {
    type: Object,
    required: true
  },
  onUpdate: {
    type: Function,
    required: true
  },
  isValid: {
    type: Boolean,
    required: true
  },
  onStepComplete: {
    type: Function,
    required: false,
    default: () => {}
  }
})

// Emit events to parent component
const emit = defineEmits(['update:isValid', 'skip-to-step'])

// State for handling loading of contract terms
const isLoading = ref(false)
const loadError = ref('')

// State variables for saving agreement have been removed since the saving is now handled by the wizard

// Initialize contract agreement if not already initialized
if (!props.formData.contractAgreement) {
  props.onUpdate({
    contractAgreement: {
      termsAccepted: false,
      termsData: null
    }
  })
}

// Step is always valid since checkbox is removed
const isStepValid = computed(() => {
  return true
})

// Update step validity when it changes
watch(isStepValid, (newValue) => {
  emit('update:isValid', newValue)
})

// Load contract terms from API
async function loadContractTerms() {
  isLoading.value = true
  loadError.value = ''
  
  try {
    // Get the authenticated user's BEE number
    const beeNumber = await GetBeeNumberAuthenicatedUser()
    
    if (!beeNumber) {
      loadError.value = t('errors.loadingContractTerms')
      logger.error('Error loading contract terms: No BEE number available')
      return
    }
    
    // Call the API to get the contract terms
    const response = await TermsNUWGet(beeNumber)
    
    console.log(response.item);
    if (response) {
      // Update the contract terms in the formData
      props.onUpdate({
        contractAgreement: {
          termsAccepted: false,
          termsData: response.item
        }
      })
      logger.info('Contract terms loaded successfully')
    } else {
      loadError.value = t('errors.loadingContractTerms')
      logger.error('Error loading contract terms: Empty response')
    }
  } catch (error) {
    loadError.value = t('errors.loadingContractTerms')
    logger.error('Error loading contract terms:', error)
  } finally {
    isLoading.value = false
  }
}


// The saveContractAgreement function has been removed since the data is now being sent with the wizard's continue button

// Check if terms already exist and load contract terms when component is mounted
onMounted(async () => {
  // Emit initial validation state (always true since checkbox is removed)
  emit('update:isValid', true)
  
  /* Comentado temporalmente para deshabilitar la validación y el salto automático
  try {
    // Set loading state in parent component
    SetIsLoading(true)
    
    // Get the authenticated user's BEE number
    const beeNumber = await GetBeeNumberAuthenicatedUser()
    
    if (!beeNumber) {
      loadError.value = t('errors.loadingContractTerms')
      logger.error('Error checking contract terms: No BEE number available')
      SetIsLoading(false)
      return
    }
    
    // Call the API to check if terms already exist
    const response = await PwsProfileTermsGetList({ beeNumber }) as any
    logger.debug("Contract terms check response:", response);
    
    // If contracts exist, skip this step
    if (response && response.items.length > 0) {
      logger.info('Contract terms already exist, skipping step')
      console.log("Contract terms already exist, skipping step");
      
      // Clear loading state
      SetIsLoading(false)
      
      // Emit event to parent to directly update the step
      // Since WizardSteps.BusinessInfo = 2 (not 1), we need to use the correct value
      emit('skip-to-step', 2) // WizardSteps.BusinessInfo = 2
      return
    }
    
    // Otherwise, load the contract terms
    await loadContractTerms()
    SetIsLoading(false)
  } catch (error) {
    loadError.value = t('errors.loadingContractTerms')
    logger.error('Error checking existing contract terms:', error)
    await loadContractTerms()
    SetIsLoading(false)
  }
  */
  
  // Simplemente cargar los términos del contrato sin verificar si ya existen
  try {
    SetIsLoading(true)
    await loadContractTerms()
  } catch (error) {
    loadError.value = t('errors.loadingContractTerms')
    logger.error('Error loading contract terms:', error)
  } finally {
    SetIsLoading(false)
  }
})
</script>

<template>
  <div class="wizard-step">
    <div class="wizard-step-header">
    </div>
    
    <div class="form-section">
      <!-- Loading state -->
      <div v-if="isLoading" class="loading-indicator">
        {{ t('labels.LoadingContractTerms') }}
      </div>
      
      <!-- Error state -->
      <div v-else-if="loadError" class="validation-message">
        {{ loadError }}
        <button @click="loadContractTerms" class="reload-button">
          {{ t('actions.TryAgain') }}
        </button>
      </div>
      
      <!-- No terms data state -->
      <div v-else-if="!formData.contractAgreement?.termsData" class="no-terms">
        {{ t('labels.NoContractTerms') }}
      </div>
      
      <!-- Terms display and agreement -->
      <template v-else>
        <!-- Contract content -->
        <div class="contract-content">
          <div v-html="formData.contractAgreement.termsData.terms" class="terms-text"></div>
        </div>
        
        <!-- The success/error alerts and save button have been removed since the data is now being sent with the wizard's continue button -->
      </template>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.wizard-step {
  margin-bottom: 25px;
}

.wizard-step-header {
  margin-bottom: 16px;
}

.wizard-step-header h3 {
  @include typography-h4;
  @include font-weight-semibold;
  margin-bottom: 6px;
}

.wizard-step-header p {
  @include typography-body;
  color: #666;
  margin-bottom: 10px;
}

.form-section {
  margin-bottom: 25px;
}

.contract-content {
  max-height: 350px;
  overflow-y: auto;
  padding: $spacing-4;
  border: 1px solid #e0e0e0;
  border-radius: $radius-sm;
  background-color: var(--vt-c-white-soft);
  margin-bottom: 16px;
}

.terms-text {
  @include typography-small;
  white-space: pre-wrap;
  line-height: 1.5;
}


.validation-message {
  @include typography-small;
  color: #e53935;
  margin-top: 4px;
}

.loading-indicator {
  margin: $spacing-4 0;
  font-style: italic;
  color: #666;
  font-size: 0.9rem;
}

.no-terms {
  @include typography-body;
  margin: $spacing-4 0;
  color: #666;
}

.reload-button {
  margin-left: 8px;
  padding: $spacing-1 $spacing-2;
  background-color: var(--vt-c-white-soft);
  border: 1px solid #e0e0e0;
  border-radius: $radius-sm;
  @include typography-small;
  cursor: pointer;
}

.form-actions {
  margin-top: 16px;
  display: flex;
  justify-content: flex-start;
}

.alert {
  padding: $spacing-2-5 $spacing-4;
  margin-bottom: 16px;
  border: 1px solid transparent;
  @include typography-body;
  border-radius: $radius-sm;
}

.alert-success {
  color: #155724;
  background-color: #d4edda;
  border-color: #c3e6cb;
}

.alert-danger {
  color: #721c24;
  background-color: #f8d7da;
  border-color: #f5c6cb;
}

.alert-dismissible {
  padding-right: 36px;
}

.alert-dismissible .btn-close {
  position: absolute;
  top: 0;
  right: 0;
  padding: $spacing-2-5 $spacing-4;
  background: transparent;
  border: 0;
  cursor: pointer;
}
</style>
